import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Euro,
  Target,
  Clock,
  Award,
  ArrowUpRight,
  ArrowDownRight,
  AlertTriangle,
  Zap,
  Settings2,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import type { LeadStatus } from "./SalesPipeline";

type OperatorPeriod = "current_month" | "3_months" | "all";

// ─── Config ───────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  INBOUND: "Entrant",
  OUTBOUND: "Sortant",
  TRAVAUX_BTP: "TravauxBTP",
  REFERRAL: "Référence",
  OTHER: "Autre",
};

// ─── Components ───────────────────────────────────────────────────────────────

function KpiCard({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  icon: Icon,
  iconColor = "text-primary",
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: "up" | "down" | "neutral";
  trendLabel?: string;
  icon: any;
  iconColor?: string;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-xs text-muted-foreground mb-1">{title}</div>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>}
            {trendLabel && (
              <div
                className={`flex items-center gap-1 text-xs mt-1 font-medium ${
                  trend === "up" ? "text-success" : trend === "down" ? "text-destructive" : "text-muted-foreground"
                }`}
              >
                {trend === "up" ? (
                  <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
                ) : trend === "down" ? (
                  <ArrowDownRight aria-hidden="true" className="h-3 w-3" />
                ) : null}
                {trendLabel}
              </div>
            )}
          </div>
          <div aria-hidden="true" className={`p-2 rounded-lg bg-muted ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniBar({ value, max, color = "bg-primary" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-2 bg-muted rounded-full overflow-hidden w-full">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const STAGE_LABELS: Record<string, string> = {
  PROSPECT: "Prospect",
  DEMO: "Démo",
  NEGOCIATION: "Négociation",
  GAGNE: "Gagné",
  PERDU: "Perdu",
};

export default function SalesPerformance() {
  const { user } = useAuth();
  const canManageTargets = user?.staffRole === "SUPER_ADMIN" || user?.staffRole === "ADMIN_OPS";

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.sales.getDashboard.useQuery();
  const { data: velocityData } = trpc.sales.getPipelineVelocity.useQuery();
  const [operatorPeriod, setOperatorPeriod] = useState<OperatorPeriod>("all");
  const { data: operatorData } = trpc.sales.getPerformanceByOperator.useQuery({ period: operatorPeriod });

  // Targets dialog state
  const currentMonthStr = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [targetsDialogOpen, setTargetsDialogOpen] = useState(false);
  const [targetMonth, setTargetMonth] = useState(currentMonthStr);
  const [targetAmounts, setTargetAmounts] = useState<Record<string, string>>({});

  const upsertTargetMutation = trpc.sales.upsertTarget.useMutation({
    onSuccess: () => {
      toast.success("Objectifs enregistrés");
      utils.sales.getDashboard.invalidate();
      utils.sales.getPerformanceByOperator.invalidate();
    },
    onError: () => toast.error("Erreur lors de l'enregistrement"),
  });

  function handleOpenTargets() {
    // Pre-fill amounts from current salesTargets
    if (data?.salesTargets) {
      const init: Record<string, string> = {};
      for (const st of data.salesTargets) {
        if (st.target > 0) init[st.name] = String(st.target);
      }
      setTargetAmounts(init);
    }
    setTargetsDialogOpen(true);
  }

  async function handleSaveTargets() {
    const operators = data?.salesTargets ?? [];
    const promises = operators
      .filter((op) => targetAmounts[op.name] !== undefined)
      .map((op) =>
        upsertTargetMutation.mutateAsync({
          userId: op.name,
          month: targetMonth,
          targetAmount: Number(targetAmounts[op.name]) || 0,
        })
      );
    await Promise.all(promises);
    setTargetsDialogOpen(false);
  }

  if (isLoading || !data) {
    return (
      <AdminLayout>
        <div role="status" className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Chargement…
        </div>
      </AdminLayout>
    );
  }

  const leads = data.leads;
  const won = leads.filter((l) => l.status === "GAGNE");
  const lost = leads.filter((l) => l.status === "PERDU");
  const active = leads.filter((l) => !["GAGNE", "PERDU"].includes(l.status));

  const totalMRR = won.reduce((s, l) => s + l.estimatedValue, 0) / 12;
  const totalARR = won.reduce((s, l) => s + l.estimatedValue, 0);
  const conversionRate = leads.length > 0 ? Math.round((won.length / leads.length) * 100) : 0;
  const avgDealSize = won.length > 0 ? Math.round(totalARR / won.length) : 0;

  const monthlyConversions = data.monthlyConversions;
  const currentMonth = monthlyConversions[monthlyConversions.length - 1];
  const prevMonth = monthlyConversions[monthlyConversions.length - 2];
  const mrrGrowth =
    prevMonth && prevMonth.mrr > 0
      ? Math.round(((currentMonth.mrr - prevMonth.mrr) / prevMonth.mrr) * 100)
      : 0;
  const maxMonthlyMRR = Math.max(...monthlyConversions.map((m) => m.mrr), 1);

  const salesTargets = data.salesTargets;
  const sourceBreakdown = data.sourceBreakdown;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 aria-hidden="true" className="h-6 w-6 text-primary" />
            Performance Sales
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            KPIs commerciaux — {currentMonth?.month || "—"}
          </p>
        </div>
        {canManageTargets && (
          <Button variant="outline" size="sm" onClick={handleOpenTargets}>
            <Settings2 className="h-4 w-4 mr-2" />
            Définir objectifs
          </Button>
        )}
      </div>

      {/* Sales Targets Dialog */}
      <Dialog open={targetsDialogOpen} onOpenChange={setTargetsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Objectifs mensuels
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Mois</label>
              <Input
                type="month"
                value={targetMonth}
                onChange={(e) => setTargetMonth(e.target.value)}
                className="mt-1.5"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Objectifs par commercial (€/an)</label>
              <div className="mt-2 space-y-2">
                {(data?.salesTargets ?? []).map((op) => (
                  <div key={op.name} className="flex items-center gap-3">
                    <span className="text-sm flex-1 truncate" title={op.name}>
                      {op.name || "Non assigné"}
                    </span>
                    <Input
                      type="number"
                      placeholder="0"
                      value={targetAmounts[op.name] ?? ""}
                      onChange={(e) =>
                        setTargetAmounts((prev) => ({ ...prev, [op.name]: e.target.value }))
                      }
                      className="w-32"
                    />
                  </div>
                ))}
                {(data?.salesTargets ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">Aucun commercial avec des leads</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTargetsDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveTargets} disabled={upsertTargetMutation.isPending}>
              {upsertTargetMutation.isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          title="MRR généré"
          value={`${Math.round(totalMRR).toLocaleString("fr-FR")} €`}
          subtitle="Revenus récurrents mensuels"
          trend="up"
          trendLabel={`+${mrrGrowth}% vs mois dernier`}
          icon={Euro}
          iconColor="text-success"
        />
        <KpiCard
          title="Taux de conversion"
          value={`${conversionRate}%`}
          subtitle={`${won.length} gagnés / ${leads.length} total`}
          trend={conversionRate >= 30 ? "up" : "down"}
          trendLabel={conversionRate >= 30 ? "Au-dessus objectif" : "Sous objectif 30%"}
          icon={Target}
          iconColor="text-primary"
        />
        <KpiCard
          title="Valeur moyenne deal"
          value={`${avgDealSize.toLocaleString("fr-FR")} €`}
          subtitle="ARR par client converti"
          trend="neutral"
          icon={Award}
          iconColor="text-pipeline-demo"
        />
        <KpiCard
          title="Pipeline actif"
          value={active.length}
          subtitle={`${active.reduce((s, l) => s + l.estimatedValue, 0).toLocaleString("fr-FR")} €/an potentiel`}
          trend="up"
          trendLabel="Leads en cours"
          icon={Users}
          iconColor="text-warning"
        />
      </div>

      {/* Pipeline Velocity */}
      {velocityData && (
        <>
          {/* Velocity KPIs */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <KpiCard
              title="Cycle de vente moyen"
              value={
                velocityData.avgTotalCycleDays > 0
                  ? `${velocityData.avgTotalCycleDays}j`
                  : "—"
              }
              subtitle="Prospect → Gagné"
              trend="neutral"
              icon={Clock}
              iconColor="text-primary"
            />
            <KpiCard
              title="Étape la plus lente"
              value={STAGE_LABELS[velocityData.slowestStage] ?? velocityData.slowestStage}
              subtitle={(() => {
                const s = velocityData.stageVelocity.find((v) => v.stage === velocityData.slowestStage);
                return s ? `${s.avgDays}j en moyenne` : undefined;
              })()}
              trend="down"
              trendLabel="Goulot d'étranglement"
              icon={TrendingDown}
              iconColor="text-warning"
            />
            <KpiCard
              title="Deals bloqués +14j"
              value={velocityData.dealsStuckOver14Days}
              subtitle="Sans changement de statut"
              trend={velocityData.dealsStuckOver14Days > 0 ? "down" : "up"}
              trendLabel={velocityData.dealsStuckOver14Days > 0 ? "À relancer" : "Aucun blocage"}
              icon={AlertTriangle}
              iconColor={velocityData.dealsStuckOver14Days > 0 ? "text-destructive" : "text-success"}
            />
          </div>

          {/* Velocity bar chart */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap aria-hidden="true" className="h-4 w-4 text-warning" />
                Pipeline Velocity — durée moyenne par étape
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const maxDays = Math.max(...velocityData.stageVelocity.map((s) => s.avgDays), 1);
                const slowest = velocityData.slowestStage;
                const fastest = velocityData.stageVelocity
                  .filter((s) => s.count > 0 && s.avgDays > 0)
                  .reduce<typeof velocityData.stageVelocity[0] | null>(
                    (a, b) => (a === null || b.avgDays < a.avgDays ? b : a),
                    null
                  );
                return (
                  <div className="flex items-end gap-3 h-36">
                    {velocityData.stageVelocity.map((s) => {
                      const pct = maxDays > 0 ? (s.avgDays / maxDays) * 100 : 0;
                      const isSlowest = s.stage === slowest && s.count > 0;
                      const isFastest = fastest !== null && s.stage === fastest.stage && s.count > 0;
                      const barColor = isSlowest
                        ? "bg-warning"
                        : isFastest
                        ? "bg-success"
                        : "bg-primary/50";
                      return (
                        <div
                          key={s.stage}
                          className="flex-1 flex flex-col items-center gap-1"
                          title={s.count > 0 ? `${s.avgDays}j en moyenne dans cette étape` : "Aucune donnée"}
                        >
                          <div className="text-xs text-muted-foreground">
                            {s.count > 0 ? `${s.avgDays}j` : "—"}
                          </div>
                          <div
                            className={`w-full rounded-t transition-all ${barColor}`}
                            style={{ height: s.count > 0 ? `${pct}%` : "4px", minHeight: 4 }}
                          />
                          <div className="text-xs text-muted-foreground text-center leading-tight">
                            {STAGE_LABELS[s.stage] ?? s.stage}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-warning" /> Étape la plus lente
                </span>
                <span className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-success" /> Étape la plus rapide
                </span>
                <span className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full bg-primary/50" /> Autres étapes
                </span>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* MRR trend chart (simplified bars) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Évolution MRR mensuel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {monthlyConversions.map((m, i) => {
                const pct = maxMonthlyMRR > 0 ? (m.mrr / maxMonthlyMRR) * 100 : 0;
                const isLast = i === monthlyConversions.length - 1;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-xs text-muted-foreground">{(m.mrr / 1000).toFixed(1)}k</div>
                    <div
                      className={`w-full rounded-t ${isLast ? "bg-primary" : "bg-primary/30"}`}
                      style={{ height: `${pct}%`, minHeight: 4 }}
                    />
                    <div className="text-xs text-muted-foreground">{m.month}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-primary" /> Mois en cours
              </span>
              <span className="flex items-center gap-1">
                <div className="h-2 w-2 rounded-full bg-primary/30" /> Mois précédents
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Source breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Performance par source</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sourceBreakdown.map((s) => (
              <div key={s.source}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium">{SOURCE_LABELS[s.source] || s.source}</span>
                  <span className="text-muted-foreground">
                    {s.won}/{s.count} leads — {s.convRate}% conv.
                  </span>
                </div>
                <MiniBar
                  value={s.convRate}
                  max={100}
                  color={s.convRate >= 40 ? "bg-success" : s.convRate >= 30 ? "bg-primary" : "bg-warning"}
                />
              </div>
            ))}
            {sourceBreakdown.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Sales team performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Performance équipe Sales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {salesTargets.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune donnée</p>
            )}
            {salesTargets.map((st) => {
              const pct = st.target > 0 ? Math.round((st.won / st.target) * 100) : 0;
              return (
                <div key={st.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{st.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {st.won}/{st.target > 0 ? st.target : "—"} objectif
                      </span>
                      {st.target > 0 && (
                        <Badge
                          variant={pct >= 100 ? "default" : pct >= 70 ? "secondary" : "outline"}
                          className="text-xs"
                        >
                          {pct}%
                        </Badge>
                      )}
                    </div>
                  </div>
                  {st.target > 0 && (
                    <MiniBar
                      value={st.won}
                      max={st.target}
                      color={pct >= 100 ? "bg-success" : pct >= 70 ? "bg-primary" : "bg-warning"}
                    />
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    {st.mrr.toLocaleString("fr-FR")} €/mois générés — {st.leads} leads traités
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Funnel summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Entonnoir de conversion</CardTitle>
          </CardHeader>
          <CardContent>
            {(["PROSPECT", "DEMO", "NEGOCIATION", "GAGNE", "PERDU"] as LeadStatus[]).map((status, i) => {
              const count = leads.filter((l) => l.status === status).length;
              const pct = leads.length > 0 ? Math.round((count / leads.length) * 100) : 0;
              const STATUS_LABELS: Record<LeadStatus, string> = {
                PROSPECT: "Prospects",
                DEMO: "Démos",
                NEGOCIATION: "Négociations",
                GAGNE: "Gagnés",
                PERDU: "Perdus",
              };
              const COLORS = [
                "bg-primary",
                "bg-pipeline-demo",
                "bg-warning",
                "bg-success",
                "bg-destructive",
              ];
              return (
                <div key={status} className="flex items-center gap-3 mb-3">
                  <div
                    className={`text-xs text-white rounded px-2 py-0.5 font-medium w-24 text-center ${COLORS[i]}`}
                  >
                    {STATUS_LABELS[status]}
                  </div>
                  <div className="flex-1">
                    <MiniBar value={count} max={leads.length} color={COLORS[i]} />
                  </div>
                  <div className="text-xs text-muted-foreground w-12 text-right">
                    {count} ({pct}%)
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* ── Operator Performance ── */}
      <OperatorPerformanceSection
        period={operatorPeriod}
        onPeriodChange={setOperatorPeriod}
        operators={operatorData?.operators ?? []}
      />

      {/* ── Win / Loss Analytics ── */}
      <WinLossSection won={won} lost={lost} />
    </AdminLayout>
  );
}

// ─── Win/Loss Analytics section ───────────────────────────────────────────────

type LeadWithReason = { closeReason?: string | null; closeNote?: string | null };

function countReasons(leads: LeadWithReason[]): Array<{ reason: string; count: number }> {
  const map = new Map<string, number>();
  for (const l of leads) {
    const key = l.closeReason ?? "Non renseigné";
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([reason, count]) => ({ reason, count }));
}

function ReasonBar({ reason, count, max, color }: { reason: string; count: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-muted-foreground w-36 truncate" title={reason}>{reason}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium w-8 text-right">{count}</span>
    </div>
  );
}

// ─── Operator Performance section ─────────────────────────────────────────────

type OperatorRow = {
  name: string;
  active: number;
  won: number;
  lost: number;
  conversionRate: number;
  mrr: number;
  pipelineValue: number;
  avgCycleDays: number;
  target: number;
};

const CONV_TARGET = 30;

function OperatorPerformanceSection({
  period,
  onPeriodChange,
  operators,
}: {
  period: OperatorPeriod;
  onPeriodChange: (p: OperatorPeriod) => void;
  operators: OperatorRow[];
}) {
  const PERIOD_LABELS: Record<OperatorPeriod, string> = {
    current_month: "Mois courant",
    "3_months": "3 mois",
    all: "Tout",
  };

  return (
    <Card className="mt-4">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          Performance par opérateur
        </CardTitle>
        <div className="flex gap-1">
          {(["current_month", "3_months", "all"] as OperatorPeriod[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPeriodChange(p)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                period === p
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Opérateur</TableHead>
              <TableHead className="text-right">Actifs</TableHead>
              <TableHead className="text-right">Gagnés</TableHead>
              <TableHead className="text-right">Perdus</TableHead>
              <TableHead>Taux conv.</TableHead>
              <TableHead className="text-right">MRR</TableHead>
              <TableHead className="text-right">Pipeline</TableHead>
              <TableHead className="text-right">Cycle moy.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune donnée
                </TableCell>
              </TableRow>
            ) : (
              operators.map((op) => (
                <TableRow key={op.name}>
                  <TableCell className="font-medium">{op.name}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{op.active}</TableCell>
                  <TableCell className="text-right text-green-700 font-medium">{op.won}</TableCell>
                  <TableCell className="text-right text-destructive">{op.lost}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            op.conversionRate >= CONV_TARGET ? "bg-success" : "bg-orange-400"
                          }`}
                          style={{ width: `${Math.min(op.conversionRate, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium">{op.conversionRate}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {op.mrr > 0 ? `${op.mrr.toLocaleString("fr-FR")} €` : "—"}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {op.pipelineValue > 0 ? `${op.pipelineValue.toLocaleString("fr-FR")} €` : "—"}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {op.avgCycleDays > 0 ? `${op.avgCycleDays}j` : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function WinLossSection({ won, lost }: { won: LeadWithReason[]; lost: LeadWithReason[] }) {
  const lossReasons = countReasons(lost);
  const winReasons = countReasons(won);
  const maxLoss = lossReasons[0]?.count ?? 1;
  const maxWin = winReasons[0]?.count ?? 1;

  if (won.length === 0 && lost.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 mt-4">
      {/* Loss reasons */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-destructive" />
            Raisons de perte
            <Badge variant="destructive" className="ml-auto text-xs">{lost.length} perdus</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {lossReasons.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune donnée</p>
          ) : (
            lossReasons.map(({ reason, count }) => (
              <ReasonBar key={reason} reason={reason} count={count} max={maxLoss} color="bg-destructive" />
            ))
          )}
        </CardContent>
      </Card>

      {/* Win reasons */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-success" />
            Raisons de gain
            <Badge className="ml-auto text-xs bg-success text-success-foreground">{won.length} gagnés</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {winReasons.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune donnée</p>
          ) : (
            winReasons.map(({ reason, count }) => (
              <ReasonBar key={reason} reason={reason} count={count} max={maxWin} color="bg-success" />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
